import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import {
  Input,
  InputGroup,
  InputLeftElement,
  InputProps,
  InputRightElement,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import Icon from '../../Icon/Icon';
import IconButton from '../../IconButton/IconButton';
import Tooltip from '../../Tooltip/Tooltip';
import { FilterStyle } from '../Filter.theme';
import { FilterValue } from '../Filter.types';

export interface FilterSearchProps extends Omit<InputProps, 'onChange' | 'value'> {
  onChange: (category: string, selected: FilterValue) => void;
  searchTooltip?: ReactNode;
  value: string;
}

const FilterSearch = (props: FilterSearchProps) => {
  const { onChange, placeholder, searchTooltip, value, ...rest } = props;
  const filterStyle = useMultiStyleConfig('Filter') as FilterStyle;

  const [searchValue, setSearchValue] = useState(value);

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.currentTarget.value);
  };

  const onClearClick = () => {
    setSearchValue('');
  };

  useEffect(() => {
    const delayInputTimeoutId = setTimeout(() => {
      onChange('search', searchValue.length ? [searchValue] : []);
    }, 500);
    return () => clearTimeout(delayInputTimeoutId);
  }, [searchValue]);

  useEffect(() => {
    setSearchValue(value);
  }, [value]);

  const inputProps: InputProps = {
    onChange: onInputChange,
    placeholder: placeholder || 'Search...',
    sx: filterStyle.searchInput,
    value: searchValue,
    ...rest,
  };
  return (
    <InputGroup height="fit-content" maxWidth="9.25rem" minWidth="6rem">
      <InputLeftElement margin="8">
        <Icon color="neutral.60" name="Magnifier" size="16" />
      </InputLeftElement>
      <Tooltip isDisabled={!searchTooltip} label={searchTooltip}>
        <Input {...inputProps} />
      </Tooltip>
      {!!searchValue && (
        <InputRightElement>
          <IconButton aria-label="Clear" iconName="Cross" onClick={onClearClick} size="sm" variant="tertiary" />
        </InputRightElement>
      )}
    </InputGroup>
  );
};

export default FilterSearch;
